import { assertNever } from 'owa-assert';
import {
    ActionType,
    AttachmentFullViewState,
    AttachmentMenuAction,
    isSaveToCloudApplicable,
} from 'owa-attachment-full-data';
import isAttachmentDownloadable from 'owa-attachment-full-data/lib/utils/isAttachmentDownloadable';
import { MailboxType } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { SurfaceAction } from '../../types/SurfaceAction';
import isMenuActionSupported from '../../utils/isMenuActionSupported';
import { isOfficeFile } from '../../utils/isOfficeFile';

export function getShouldHideSurfaceActionButton(
    action: SurfaceAction,
    attachmentViewState: AttachmentFullViewState
): boolean {
    // We hide all buttons regardless of action when there is an ongoing action, or the strategy says to
    if (
        attachmentViewState.ongoingAction !== ActionType.None ||
        !attachmentViewState.strategy.showActionsMenu
    ) {
        return true;
    }

    switch (action) {
        // If we show the addToCalendar button it means that the feature is supported.
        case SurfaceAction.addToCalendar:
            return false;
        case SurfaceAction.download:
            return (
                !isMenuActionSupported(AttachmentMenuAction.Download, attachmentViewState) ||
                !isAttachmentDownloadable(attachmentViewState)
            );
        case SurfaceAction.saveToCloud:
        case SurfaceAction.openNewTabAndSaveToCloud:
            return (
                !attachmentViewState.isReadOnly ||
                !isSaveToCloudAvailable(attachmentViewState, action)
            );
        case SurfaceAction.editAndReply:
            return (
                !attachmentViewState.isReadOnly || !isOfficeFile(attachmentViewState.attachmentId)
            );
        default:
            return assertNever(action);
    }
}

function isSaveToCloudAvailable(
    attachmentViewState: AttachmentFullViewState,
    action: SurfaceAction
): boolean {
    const isConsumerGroup =
        isConsumer() &&
        attachmentViewState.attachmentId.mailboxInfo?.type === MailboxType.GroupMailbox;
    let featureEnabled = false;
    if (action === SurfaceAction.openNewTabAndSaveToCloud) {
        featureEnabled =
            isFeatureEnabled('woven-newTabExperiment') &&
            isOfficeFile(attachmentViewState.attachmentId);
    } else {
        featureEnabled =
            isFeatureEnabled('woven-saveToCloud2-viewInOneDrive') ||
            isFeatureEnabled('woven-saveToCloud2-editInOneDrive') ||
            isFeatureEnabled('woven-saveToCloud2-editInApp') ||
            isFeatureEnabled('woven-saveToCloud2-editOnline') ||
            isFeatureEnabled('woven-saveToCloud2-openFolder') ||
            isFeatureEnabled('woven-newTabExperiment');
    }

    return (
        featureEnabled &&
        isMenuActionSupported(AttachmentMenuAction.SaveToCloud, attachmentViewState) &&
        isSaveToCloudApplicable(attachmentViewState) &&
        !isConsumerGroup
    );
}
